const DEFAULT_PLAN_FORM = Object.freeze({
  name: "", currency: "INR", price: "", taxMode: "exclusive", billingInterval: "event", stripePriceId: "",
  storageGb: "", maxPhotos: "", maxGuests: "", maxPhotographers: "1", maxEventDurationHours: "", retentionDays: "90", retentionExtensionDays: "0",
  faceSearch: true, originalDownloads: true, supportLevel: "standard", features: "",
  upgrades: "Not applicable", downgrades: "Not applicable", cancellation: "Contact support", refunds: "Refer to the agreed refund policy"
});

export const PLAN_PRESETS = Object.freeze({
  low: Object.freeze({ name: "Low", price: "999.00", storageGb: "50", maxPhotos: "2000", maxGuests: "250", maxPhotographers: "1", maxEventDurationHours: "24", retentionDays: "90", supportLevel: "standard", features: "Private event gallery\nAI face search\nOriginal-quality downloads" }),
  medium: Object.freeze({ name: "Medium", price: "2499.00", storageGb: "200", maxPhotos: "8000", maxGuests: "1000", maxPhotographers: "3", maxEventDurationHours: "72", retentionDays: "180", supportLevel: "priority", features: "Everything in Low\nMulti-photographer workflow\nPriority support\nExtended gallery retention" }),
  high: Object.freeze({ name: "High", price: "4999.00", storageGb: "500", maxPhotos: "20000", maxGuests: "3000", maxPhotographers: "8", maxEventDurationHours: "168", retentionDays: "365", supportLevel: "dedicated", features: "Everything in Medium\nLarge multi-day events\nDedicated support\nOne-year gallery retention" })
});

export function makeForm(version, code) {
  if (!version) return { ...DEFAULT_PLAN_FORM, ...(PLAN_PRESETS[code] || {}) };
  return {
    ...DEFAULT_PLAN_FORM,
    name: version.name || "", currency: version.currency || "INR",
    price: version.amountMinor == null ? "" : (version.amountMinor / 100).toFixed(2),
    taxMode: version.taxMode || "exclusive", billingInterval: version.billingInterval || "event",
    stripePriceId: version.stripePriceId || "",
    storageGb: version.entitlements?.storageBytes == null ? "" : String(Number((version.entitlements.storageBytes / 1073741824).toFixed(2))),
    maxPhotos: String(version.entitlements?.maxPhotos ?? ""), maxGuests: String(version.entitlements?.maxGuests ?? ""),
    maxPhotographers: String(version.entitlements?.maxPhotographers ?? 1),
    maxEventDurationHours: String(version.entitlements?.maxEventDurationHours ?? ""),
    retentionDays: String(version.entitlements?.retentionDays ?? 90), retentionExtensionDays: String(version.entitlements?.retentionExtensionDays ?? 0),
    faceSearch: Boolean(version.entitlements?.faceSearch), originalDownloads: version.entitlements?.originalDownloads !== false,
    supportLevel: version.entitlements?.supportLevel || "standard",
    features: (version.entitlements?.features || []).join("\n"),
    upgrades: version.policies?.upgrades || "", downgrades: version.policies?.downgrades || "",
    cancellation: version.policies?.cancellation || "", refunds: version.policies?.refunds || ""
  };
}

export function toPlanPayload(form, expectedRevision) {
  const price = String(form.price || "").trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(price)) throw new Error("Enter a valid price with at most two decimal places.");
  const amountMinor = Math.round(Number(price) * 100);
  if (!Number.isSafeInteger(amountMinor)) throw new Error("Plan price is too large.");
  const storage = Number(form.storageGb);
  if (!Number.isFinite(storage) || storage < 0 || !Number.isSafeInteger(Math.round(storage * 1073741824))) {
    throw new Error("Enter a valid non-negative storage allowance in GB.");
  }
  const integer = (key, minimum = 0) => {
    const raw = String(form[key] ?? "").trim();
    const value = Number(raw);
    if (!raw || !Number.isSafeInteger(value) || value < minimum) throw new Error(`Enter a valid ${key} (minimum ${minimum}).`);
    return value;
  };
  const stripePriceId = String(form.stripePriceId || "").trim();
  if (!/^price_[A-Za-z0-9]+$/.test(stripePriceId)) throw new Error("Enter the corresponding Stripe Price ID (price_...).");
  for (const field of ["upgrades", "downgrades", "cancellation", "refunds"]) {
    if (String(form[field] || "").trim().length < 3) throw new Error(`Enter the ${field} policy (at least 3 characters).`);
  }
  const features = String(form.features || "").split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean);
  if (features.length > 50 || features.some((item) => item.length > 160)) throw new Error("Enter up to 50 features, max 160 characters each.");
  return {
    expectedRevision, name: String(form.name || "").trim(), currency: String(form.currency).toUpperCase(),
    amountMinor, taxMode: form.taxMode, billingInterval: form.billingInterval, stripePriceId,
    entitlements: {
      storageBytes: Math.round(storage * 1073741824), maxPhotos: integer("maxPhotos"), maxGuests: integer("maxGuests"),
      maxPhotographers: integer("maxPhotographers", 1), maxEventDurationHours: integer("maxEventDurationHours", 1),
      retentionDays: integer("retentionDays", 1), retentionExtensionDays: integer("retentionExtensionDays"),
      faceSearch: Boolean(form.faceSearch), originalDownloads: Boolean(form.originalDownloads),
      supportLevel: form.supportLevel, features
    },
    policies: {
      upgrades: form.upgrades.trim(), downgrades: form.downgrades.trim(), cancellation: form.cancellation.trim(), refunds: form.refunds.trim()
    }
  };
}
